import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { KTSVG } from '../../../_metronic/helpers'
import { useHistory, useParams } from 'react-router-dom'
import { ExperiencePreviewGallery } from './components/ExperiencePreviewGallery'
import { ExperiencePreviewInviteCarousel } from './components/ExperiencePreviewInviteCarousel'
import { getAudiences, getDetailExperience, getDurations } from '../../services/teamExperienceService'
import { GenericModel } from '../teamExperiences/models/GenericModel'
import { ExperienceShort, ExperienceDetail } from '../teamExperiences/models/ExperienceSummaryModel'
import { formatDateShortIntl2, formatDateStrShort, getOnlyDate } from '../../helpers/FormatDate'
import { SectionInfoTabs } from './components/SectionInfoTabs'
import { LoaderOverlay } from '../../hooks/loader/LoaderOverlay'
import { DeclineExperienceModal } from '../experiencesAdmin/components/DeclineExperienceModal'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'
import { approveExperience, getExperiencesByStatus, updateExperienceStatus } from '../../services/adminExperiencesService'
import { handleApiError } from '../../utils/handleApiError'
import { showErrorAlert } from '../../helpers/alert'
import { EXPERIENCE_STATUS } from '../teamExperiences/models/ExperienceStatus'
import { UserModel } from '../../modules/auth/models/UserModel'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../setup'
import { UserRole } from '../../constants/roles'
import { getMaxPeople, hasDescriptionText } from './components/utils'
import { defaultProfileImage } from '../../utils/constants'
import { hasRole } from '../../utils/permissions'
import { handleImageErrorBlack } from '../../utils/UtilsFiles'
import { getYouTubeId } from '../../utils/Video'
import { formatDurationLabel, formatUsdAmount, getTimeDifference, pickDurationName } from '../../utils/dateValidations'
import { getSearchableExperiences } from '../../services/membersService'
import { ActionOption, GenericDropdown } from '../../hooks/utils/GenericDropdown'
import GenericTooltip from '../../hooks/utils/GenericTooltip'

const ExperiencePreviewPage: FC = () => {
    const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const history = useHistory()
    const { id } = useParams();
    const [durations, setDuration] = useState<GenericModel>([]);
    const [audiences, setAudiences] = useState<GenericModel>([]);
    const [experience, setExperience] = useState<ExperienceDetail>({});
    const [experiences, setExperiences] = useState<ExperienceShort[]>([]);
    const [isLoader, setIsLoader] = useState(true);
    const [messageLoader, setMessageLoader] = useState("Loading...");
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [openDeclineModal, setOpenDeclineModal] = useState(false);
    const confirm = useConfirmAction()
    const [isMine, setIsMine] = useState(false)
    const isFinalUser = hasRole(user, UserRole.FINAL_USER)
    const canApprove = hasRole(user,UserRole.ADMIN) || hasRole(user,UserRole.STAFF_USER)
    const [more, setMore] = useState(false); // mostrar mas ideal audience
    const [dates, setDates] = useState<ActionOption[]>([]);
    const [firstDate, setFirstDate] = useState('');
    // UAT-212 P2: which date's capacity label to show (default the first date).
    const [selectedDateIndex, setSelectedDateIndex] = useState(0);
    const [hasBooked, setHasBooked] = useState(false);
    const [bookingMessage, setBookingMessage] = useState('');
    const [bookedButtonText, setBookedButtonText] = useState('You have already purchased this experience.');

    useEffect(() => {
        if(!id){
            history.goBack();
            return;
        }
        restoreRef.current = config
        setLayout({
            ...config,
            header: {...config.header, display: false },
            toolbar: {...config.toolbar, display: false },
            aside: {...config.aside, display: false },
            footer: {...config.footer, display: true },
            main: {
                ...config.main,
                body: {...config.main?.body, class: 'app-blank'},
            },
        })
        document.body.classList.add('bg-experience-preview');
        document.getElementById('kt_footer')?.classList.add('bg-experience-preview');

        const fetchData = async () => {
            const audience = await getAudiences();
            setAudiences(audience.results)
            const duration = await getDurations();
            setDuration(duration?.results || [])
            
        }
        fetchData();
    }, [])

    useEffect(() => {
        // aquí vuelves a cargar la info del experience
        getDetails();
    }, [id]);

    const checkBookingStatus = (exp: ExperienceDetail) => {
        if (exp?.hasBooked && exp.hasBooked.isBooked) {
            const hasBookedObj = exp.hasBooked;
            const expDates: any[] = exp.dates || [];
            const confirmedList: any[] = hasBookedObj.confirmedDates || (hasBookedObj.isConfirmed ? hasBookedObj.dates : []);
            const pendingList: any[] = hasBookedObj.pendingDates || (hasBookedObj.isPending ? hasBookedObj.dates : []);

            if (expDates.length === 0) {
                if (hasBookedObj.isPending && !hasBookedObj.isConfirmed) {
                    setBookingMessage("You have a booking request pending host confirmation.");
                    setBookedButtonText("Booking request pending host confirmation");
                } else {
                    setBookingMessage("You have already purchased this experience.");
                    setBookedButtonText("You have already purchased this experience.");
                }
                setHasBooked(true);
            } else {
                const matchingConfirmed = expDates.filter((d: any) => {
                    const onlyDate = getOnlyDate(d.start);
                    return confirmedList.includes(onlyDate) || confirmedList.includes(d.id);
                });
                const matchingPending = expDates.filter((d: any) => {
                    const onlyDate = getOnlyDate(d.start);
                    return pendingList.includes(onlyDate) || pendingList.includes(d.id);
                });

                const totalMatched = matchingConfirmed.length + matchingPending.length;

                if (totalMatched > 0) {
                    if (totalMatched < expDates.length) {
                        const messages: string[] = [];
                        if (matchingConfirmed.length > 0) {
                            messages.push(`Purchased dates: ${matchingConfirmed.map((d: any) => formatDateStrShort(d.start)).join(', ')}`);
                        }
                        if (matchingPending.length > 0) {
                            messages.push(`Pending confirmation: ${matchingPending.map((d: any) => formatDateStrShort(d.start)).join(', ')}`);
                        }
                        setBookingMessage(messages.join(' | '));
                        setHasBooked(false);
                    } else {
                        if (matchingPending.length > 0 && matchingConfirmed.length === 0) {
                            setBookingMessage("You have a booking request pending host confirmation.");
                            setBookedButtonText("Booking request pending host confirmation");
                        } else {
                            setBookingMessage("You have already purchased this experience.");
                            setBookedButtonText("You have already purchased this experience.");
                        }
                        setHasBooked(true);
                    }
                } else {
                    setHasBooked(false);
                    setBookingMessage('');
                }
            }
        } else {
            setHasBooked(false);
            setBookingMessage('');
        }
    };

    const getDetails = async () => {
        setIsLoader(true);
        const experience_ = await getDetailExperience(id);
        if(experience_?.id){
            setExperience(experience_);
            setIsMine(experience_?.host?.id == user?.id)
            setExperiences(experience_?.moreExperiences || []);

            // Validar si el usuario ya reservó fechas
            checkBookingStatus(experience_);

            setIsLoader(false);
        }else{
            setIsLoader(false);
            history.goBack();
        }

       let dates_:ActionOption[] = [];
        let firstDate_ = '';
        (experience_.dates ?? []).map((item,index)=>{
            let durations = getTimeDifference(item)
            let label = (formatDateShortIntl2(item.end) == formatDateShortIntl2(item.start) ? formatDateStrShort(item.start) : `${formatDateStrShort(item.start)} - ${formatDateStrShort(item.end)}`) + ' (' + durations + ')';
            if(index==0){
                firstDate_ = label;
                setFirstDate(firstDate_);
            }
            // UAT-212 P2: render ALL dates (including the first) so each is
            // selectable and its per-date capacity label can display.
            dates_.push({
                id: index,
                label: label,
                onClick: () => {
                    setSelectedDateIndex(index);
                    setFirstDate(label);
                }
            })
        })
        setDates(dates_);
        setSelectedDateIndex(0);
    }

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
        video.play();
        setIsPlaying(true);
        } else {
        video.pause();
        setIsPlaying(false);
        }
    };

    const havemp4 = (link:string) => {
        return experience.optionalVideoLink?.includes('.mp4') ? true : false;
    }

    const isPartnerExperience = (exp: any): boolean => {
        if (!exp) return false;

        // 1. Check host roles
        const hostRoles = exp.host?.roles || [];
        const hostIsPartner = hostRoles.some((r: any) => {
            const code = typeof r === 'string' ? r : r?.code;
            return String(code).toUpperCase() === UserRole.PARTNER;
        });
        if (hostIsPartner) return true;

        // 2. Check submittedBy / submitted_by roles or companyName
        const submitter = exp.submittedBy || exp.submitted_by;
        if (submitter) {
            const submitterRoles = submitter.roles || [];
            const submitterIsPartner = submitterRoles.some((r: any) => {
                const code = typeof r === 'string' ? r : r?.code;
                return String(code).toUpperCase() === UserRole.PARTNER;
            });
            if (submitterIsPartner) return true;

            if (submitter.companyName || submitter.company_name) {
                return true;
            }
        }

        // 3. Check suggest / suggest_id / suggestId
        if (Boolean(exp.suggest || exp.suggest_id || exp.suggestId)) {
            return true;
        }

        // 4. Check hostType / host_type
        if (exp.hostType === 'partner' || exp.host_type === 'partner') {
            return true;
        }

        // 5. Check partner profile on host
        if (exp.host?.partnerProfile || exp.host?.partner_profile) {
            return true;
        }

        return false;
    };

    const hasAssignedMemberHost = (exp: any): boolean => {
        if (!exp?.host || !exp.host.id) return false;

        const hostRoles = exp.host?.roles || [];
        const isHostPartner = hostRoles.some((r: any) => {
            const code = typeof r === 'string' ? r : r?.code;
            return String(code).toUpperCase() === UserRole.PARTNER;
        }) || Boolean(exp.host?.partnerProfile || exp.host?.partner_profile);

        if (isHostPartner) return false;

        const isHostMember = hostRoles.some((r: any) => {
            const code = typeof r === 'string' ? r : r?.code;
            return String(code).toUpperCase() === UserRole.FINAL_USER;
        });

        const submitter = exp.submittedBy || exp.submitted_by;
        const isDifferentFromSubmitter = submitter?.id ? exp.host.id !== submitter.id : true;

        return (isHostMember || exp.hostType === 'member' || exp.host_type === 'member') && isDifferentFromSubmitter;
    };

    const onApprovedHandle = async () => {
        const response = await confirm({
            title: 'Confirm',
            message: `Do you want to approve the experience '${experience.title}'?`,
        });
        if (response) {
            try {
                setIsLoader(true);
                let isApproved: any;
                if(experience.status == EXPERIENCE_STATUS.PENDING){
                    const isPartner = isPartnerExperience(experience);
                    const hasMemberHost = hasAssignedMemberHost(experience);
                    const status_ = isPartner && !hasMemberHost ? EXPERIENCE_STATUS.PRE_LAUNCH : EXPERIENCE_STATUS.PUBLISHED;
                    isApproved = await updateExperienceStatus(experience.id, { status: status_ });
                }else if(experience.status == EXPERIENCE_STATUS.PRE_LAUNCH){
                    isApproved = await approveExperience(experience.id, {});
                }
                setIsLoader(false);
                history.goBack();
                showErrorAlert({ title: '', message: isApproved?.detail ?? 'Experience approved successfully.' });
            } catch (error) {
                setIsLoader(false);
                handleApiError(error, {
                    onServerError: ()=>showErrorAlert({ title: 'Error', message: 'Error approving the experience. Please try again later.' }),
                });
            }
        }
    }

    const havePermission = (
        experience?.status && 
        (experience?.status == EXPERIENCE_STATUS.PENDING || experience?.status == EXPERIENCE_STATUS.PRE_LAUNCH) && 
        (hasRole(user, UserRole.ADMIN) || hasRole(user, UserRole.STAFF_USER) || hasRole(user, UserRole.PROFILE_MANAGEMENT))
    );

    const typePurchase = () => {
        if(experience?.availabilityType == 'by_date'){
            history.push(`/experience/booking/${experience?.id}`)
            return;
        }else if(experience?.availabilityType == 'by_request'){
            showErrorAlert({title:'Coming Soon',message:'Availability type request'});
            return;
        }else if(experience?.availabilityType == 'by_auction'){
            showErrorAlert({title:'Coming Soon',message:'Availability type auction'});
            return;
        }
    }
    
    const getDisplayedText = (text: string, long = 250) => {
        if (more) return text;
        if (text.length > long) return text.slice(0, long) + "...";
        return text;
    };

    const locationText = experience?.hasBooked?.isBooked
        ? `${experience?.city || ''}`.trim() 
        : (experience?.city || '').trim();

    return (!experience?.id) ? (<LoaderOverlay visible={true} message={'Loading...'} />) : (<div className='tap-flex-center'>
        <div className="experience-preview-wrapper">
            <div className='experience-preview-wrapper__img'>
                <img className="" src={experience.coverImage} onError={handleImageErrorBlack} alt={'exp.title'}/>
            </div>
            {/* header */}
            {!isFinalUser && (
                <div className={`experience-preview-leadbar`}>
                    <div className='experience-preview-leadbar-content'>
                        <div className="experience-preview-leadbar-btn-secondary" onClick={()=>history.goBack()}>
                            <div className="experience-preview-leadbar-btn-text tap-flex-center w-100">
                                <div>go back</div>
                            </div>
                        </div>
                        <div className="experience-preview-leadbar-textblock">
                            <div className="experience-preview-leadbar-subtitle">
                            Experience By {experience?.host?.firstName} {experience?.host?.lastName}
                            </div>
                        </div>
                        {canApprove && (<div className={`experience-preview-leadbar-btn-secondary ${havePermission  ? 'd-flex' : 'd-none'}`} onClick={()=>setOpenDeclineModal(true)}>
                            <div className="experience-preview-leadbar-btn-text tap-flex-center w-100">
                                <div>Decline</div>
                                &nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                            </div>
                        </div>)}
                        {canApprove && (<div className={`experience-preview-leadbar-btn-primary ${havePermission ? 'd-flex' : 'd-none'}`} onClick={onApprovedHandle}>
                            <div className="experience-preview-leadbar-btn-text-primary tap-flex-center w-100">
                                <div>Approve</div>
                                &nbsp;<KTSVG path='/media/svg/nobilis/vector02.svg' />
                            </div>
                        </div>)}
                    </div>
                </div>
            )}
            <div className={`experience-preview-content ${isFinalUser ? 'experience-preview-content--no-menu' : ''}`}>

                {/* LEFT CONTENT BLOCK */}
                <div className="experience-preview-left">
                    <div className="experience-preview-header">
                        {isFinalUser && (
                            <div className="help-center-sections w-100 tap-flex-center justify-content-start mb-6">
                                <div
                                    className="nb-link-underline-500 w-auto cursor-pointer text-white"
                                    style={{ color: '#FFFFFF', borderColor: '#FFFFFF', background: 'transparent' }}
                                    onClick={() => history.goBack()}
                                >
                                    Back
                                </div>
                            </div>
                        )}
                        <div className="experience-preview-badge">
                            <div className='line-svg'>
                                <KTSVG path='/media/svg/nobilis/teams_and_partner/mask-success-line-icon.svg' />
                            </div>
                            &nbsp;&nbsp;New&nbsp;&nbsp;
                            <div className='line-svg'>
                                <KTSVG path='/media/svg/nobilis/teams_and_partner/mask-success-line-icon.svg' />
                            </div>
                        </div>

                        <h1 className="experience-preview-title">
                            {experience?.title}
                        </h1>

                        <div className="experience-preview-categories">
                            {(experience?.categories ?? []).map((cat,index)=>(
                                <span key={`item-category-${index}`} className="experience-preview-category">{cat.name}</span>
                            ))}
                        </div>
                    </div>

                    <div className="experience-preview-description-block">
                        {/* UAT-211: hide the short description (and its read-more) when empty, rather
                            than showing a "Pending information" placeholder. The audience/family row
                            below stays. */}
                        {hasDescriptionText(experience?.description) && (
                            <>
                                <p className="experience-preview-description">
                                    {getDisplayedText(experience?.description ?? '')}
                                </p>
                                {(experience?.description?.length ?? 0) > 250 && (
                                    <button className="experience-preview-readmore" onClick={() => setMore(!more)}>
                                        Read {more ? "less" : "more"}
                                    </button>)}
                            </>
                        )}
                        <div className="experience-preview-family">
                            <span className="experience-preview-family-text">{audiences.map((item)=>(item.id == experience?.audienceType ? item.name : ''))}</span>
                            <span className="experience-preview-icon-default">
                                <GenericTooltip
                                    title=""
                                    message={experience?.audienceType == 2
                                        ? "This experience welcomes adult members only, aged 18 and above."
                                        : "This experience welcomes children and family members of all ages. Infants under 2 are free of charge."}
                                    position="top"
                                    theme="light"
                                    iconPath="/media/svg/nobilis/info.svg"
                                />
                            </span>
                        </div>
                    </div>
                </div>

                {/* BOTTOM INFO ROW */}
                <div className="experience-preview-bottom">
                    <div className="experience-preview-info-left">
                        <div className="experience-preview-invited">
                            <span className="experience-preview-invited-label">Invited by</span>
                            <div className="experience-preview-invited-item">
                                <img src={experience?.host?.profilePicture} onError={(e)=>{e.currentTarget.src=defaultProfileImage}} />
                                <span>{experience?.host?.firstName} {experience?.host?.lastName}</span>
                            </div>
                        </div>

                        <div>
                            <div className="experience-preview-details">
                                {locationText.length > 0 && (
                                    <div className="experience-preview-detail">
                                        <span className="experience-preview-icon-18">
                                            <KTSVG path='/media/svg/nobilis/location-white.svg' />
                                        </span>
                                        <span className='labels'>{locationText}</span>
                                    </div>
                                )}

                                {experience?.availabilityType === 'by_date' && firstDate && (
                                    <div className="experience-preview-detail">
                                        <span className="experience-preview-icon-18">
                                            <KTSVG path='/media/svg/nobilis/calendar-white.svg' />
                                        </span>
                                        <div className='labels'>
                                            <span>{firstDate}</span>
                                        </div>
                                    </div>
                                )}

                                {experience?.capacityLimitByDates?.[selectedDateIndex]?.remainingCapacityLabel && (
                                    <div className="experience-preview-detail">
                                        <span className="experience-preview-icon-18">
                                            <KTSVG path='/media/svg/nobilis/people-white.svg' />
                                        </span>
                                        <span className='labels'>{experience?.capacityLimitByDates?.[selectedDateIndex]?.remainingCapacityLabel}</span>
                                    </div>
                                )}

                                {experience?.availabilityType === 'by_request' && experience?.duration && (
                                    <div className="experience-preview-detail"> 
                                        <span className={`experience-preview-icon-18`}>
                                            <KTSVG path='/media/svg/nobilis/clock-white.svg' />
                                        </span>
                                        <span className='labels'>
                                            {formatDurationLabel(experience.duration, pickDurationName(durations, experience.durationType))}
                                        </span>
                                    </div>
                                )}

                                {experience?.pricePerGuest && (
                                    <div className="experience-preview-detail">
                                        <span className="experience-preview-icon-18">
                                            <KTSVG path='/media/svg/nobilis/currency-white.svg' />
                                        </span>
                                        <span className='labels'>{formatUsdAmount(experience.pricePerGuest)}&nbsp;/&nbsp;{experience?.priceType === 2 || (experience as any)?.price_type === 2 ? 'Group' : 'Guest'}</span>
                                    </div>
                                )}

                            </div>
                            {experience?.availabilityType === 'by_date' && dates.length > 1 && (
                                <div className='experience-preview-details-bottom'>
                                    <div className="experience-preview-detail w-100"><span>&nbsp;</span><span>&nbsp;</span></div>
                                    <div className="experience-preview-detail w-100">
                                        <GenericDropdown className='dropdown-black'
                                            options={dates}
                                            placeholder={`(+ ${dates.length} more dates)`}
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => option.id}
                                        />
                                    </div>
                                    <div className="experience-preview-detail w-100"><span>&nbsp;</span><span>&nbsp;</span></div>
                                    <div className="experience-preview-detail w-100"><span>&nbsp;</span><span>&nbsp;</span></div>
                                </div>
                            )}
                        </div>

                    </div>
                    
                    {((!hasBooked && !canApprove && !isMine) || hasBooked) && (
                        <div className="experience-preview-actions">
                            {!hasBooked && !canApprove && !isMine && (
                                experience?.isFull ? (
                                    <button className="experience-preview-btn-secondary tap-flex-center" disabled style={{ opacity: 0.7, cursor: 'not-allowed' }}>
                                        Fully Booked
                                    </button>
                                ) : (
                                    <button className="experience-preview-btn-primary" onClick={typePurchase}>
                                        RSVP
                                        &nbsp;<KTSVG path='/media/svg/nobilis/vector1.svg' />
                                    </button>
                                )
                            )}

                            {!hasBooked && !canApprove && !isMine && (<button className="experience-preview-btn-secondary tap-flex-center">
                                add to wishlist
                                &nbsp;<KTSVG path='/media/svg/nobilis/heart-outline-white.svg' />
                            </button>)}
                            {hasBooked ? (<button className="experience-preview-btn-secondary tap-flex-center">{bookedButtonText}</button>) : null}
                            {bookingMessage && (
                            <div className="text-white mt-4 text-center" style={{ fontFamily: 'Satoshi, sans-serif', fontSize: '14px', fontWeight: 300 }}>
                                {bookingMessage}
                            </div>
                        )}
                        </div>
                    )}
                </div>

                {/* carrousel */}
                <ExperiencePreviewGallery images={experience.galleryImages ?? []} eventsScroll={experience?.galleryImages?.length>3} />

                {/* Tabs descripciones */}
                <SectionInfoTabs experience={experience} />

                {/* video */}
                <div className={`experience-preview-image-block tap-flex-center ${havemp4(experience.optionalVideoLink) ? 'd-flex' : 'd-none'}`}>
                    <video className={`experience-preview-image-block-mainimg`} 
                        ref={videoRef} controls={false}
                        onClick={togglePlay} 
                        onPause={() => setIsPlaying(false)}
                        onPlay={() => setIsPlaying(true)}
                        src={experience.optionalVideoLink} />
                    {!isPlaying && (<div className="experience-preview-image-block-icon" onClick={togglePlay}>
                        <span className="experience-preview-icon-79">
                            <KTSVG path='/media/svg/nobilis/play-white.svg' />
                        </span>
                        {/* <div className="experience-preview-image-block-icon-shape"></div> */}
                    </div>)}
                </div>
                <div className={`experience-preview-image-block tap-flex-center ${getYouTubeId(experience.optionalVideoLink) ? 'd-flex' : 'd-none'}`}>
                    <div className='experience-preview-image-block-mainimg' style={{ position: 'relative' }}>
                        <iframe
                            src={`https://www.youtube.com/embed/${getYouTubeId(experience.optionalVideoLink)}?controls=0&showinfo=0&modestbranding=1`}
                            frameBorder='0'
                            allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                            allowFullScreen
                        />
                        <div onClick={() => { window.open(experience.optionalVideoLink, '_blank') }} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 1}}/></div>
                </div>

                <div className={`experience-preview-invite-by ${experiences?.length > 0 ? '' : 'd-none'}`}>
                    <div className="experience-preview-name">
                        <KTSVG path='/media/svg/nobilis/mark-292928.svg' />
                        {experience?.host?.firstName ?? ''} {experience?.host?.lastName ?? ''}
                    </div>
                    <div className="experience-preview-invites">Invites You</div>
                    <div className="experience-preview-outline-box"></div>
                </div>
                {/* carrousel 2 */}
                <ExperiencePreviewInviteCarousel items={experiences} eventsScroll={false} />
            </div>
        </div>
        <LoaderOverlay visible={isLoader} message={messageLoader} />
        {havePermission && (<DeclineExperienceModal open={openDeclineModal} onLoad={setIsLoader} experienceId={experience.id} onCancel={()=>{setOpenDeclineModal(false)}} />)}
    </div>)
}

export { ExperiencePreviewPage }

